import { useNavigate } from '@tanstack/react-router';
import type { User } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/auth-context';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

function useNavigateAfterAuth(user: User | null, authLoading: boolean): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user) {
      void navigate({ to: '/cases', replace: true });
    }
  }, [authLoading, navigate, user]);
}

export function LoginForm() {
  const { user, loading: authLoading, signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useNavigateAfterAuth(user, authLoading);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      void navigate({ to: '/cases' });
    } catch {
      setError('Feil e-postadresse eller passord. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch {
      setError('Google-innlogging feilet. Prøv igjen.');
      setLoading(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
      <Input
        id="email"
        type="email"
        label="E-postadresse"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="deg@firma.no"
        required
        autoComplete="email"
        autoFocus
      />
      <Input
        id="password"
        type="password"
        label="Passord"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="••••••••"
        required
        autoComplete="current-password"
      />
      {error && (
        <p className="rounded-md border border-danger/20 bg-danger/8 px-3 py-2 text-xs text-danger/90">
          {error}
        </p>
      )}
      <Button type="submit" disabled={loading} className="mt-2 w-full">
        {loading ? 'Logger inn…' : 'Logg inn'}
      </Button>
      <div className="flex items-center gap-3 py-1">
        <span className="h-px flex-1 bg-[rgba(255,255,255,0.08)]" />
        <span className="text-xs text-text-tertiary">eller</span>
        <span className="h-px flex-1 bg-[rgba(255,255,255,0.08)]" />
      </div>
      <Button
        type="button"
        variant="outline"
        disabled={loading}
        className="w-full"
        onClick={() => void handleGoogleSignIn()}
      >
        Logg inn med Google
      </Button>
    </form>
  );
}
